import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  MapPin,
  ArrowRight,
  Tag,
  Layers,
  Sparkles,
  ListChecks,
  ShoppingCart,
  Weight,
  Boxes as StackBoxes,
  Anchor,
  Clock,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Crespières ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Vos cartons livrés {city.aCity} à l&apos;adresse de votre
              choix
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que les semaines précédant le jour J sont
              rythmées par de nombreuses démarches administratives,
              notre service logistique de proximité simplifie votre
              organisation en prenant en charge le transport de votre
              matériel.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture locale réactive",
                body: "Que vous résidiez dans le centre historique du village ou dans les secteurs résidentiels environnants, nous livrons vos fournitures directement à votre adresse à Crespières, ainsi que dans les communes voisines comme Feucherolles, Alluets-le-Roi, Mareil-sur-Mauldre et Thoiry.",
                Icon: MapPin,
              },
              {
                title: "Confort et gain de temps",
                body: "Vos cartons vous parviennent à plat, parfaitement propres et secs. Vous vous évitez ainsi la corvée de surcharger votre véhicule personnel et pouvez commencer vos colis immédiatement, dès la réception.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Résistance charges lourdes (split + spec sheet) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="overline" style={{ color: "#237cb5" }}>
              Charges lourdes
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b] leading-tight">
              Une résistance adaptée aux charges importantes
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Le transport de certains objets denses ou lourds
              nécessite des structures de cartons renforcées pour
              éviter les accidents lors des manutentions.
            </p>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Cette robustesse exceptionnelle garantit
              l&apos;intégrité de vos biens les plus lourds tout au
              long du trajet. En choisissant ces modèles renforcés,
              vous facilitez également le travail des déménageurs et
              éliminez le stress du carton qui cède au moment
              d&apos;être soulevé, assurant ainsi une transition
              fluide vers votre nouveau foyer.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Card 1 : Double cannelure spec sheet */}
            <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 overflow-hidden">
              <span className="absolute top-0 left-0 h-1.5 w-full bg-[#237cb5]" />
              <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#237cb5] mb-3">
                Spec · Double cannelure
              </div>
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <Weight className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                Qualité industrielle
              </h3>
              <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                Nos cartons spécial charges lourdes sont conçus pour
                supporter des éléments volumineux, des livres ou de
                la vaisselle sans jamais se déformer ni se déchirer
                sous le poids.
              </p>
            </div>

            {/* Card 2 : Empilage sécurisé */}
            <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 overflow-hidden">
              <span className="absolute top-0 left-0 h-1.5 w-full bg-[#FF5722]" />
              <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#FF5722] mb-3">
                Spec · Empilage
              </div>
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#FF5722] text-white">
                <StackBoxes className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                Géométrie stable
              </h3>
              <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                Grâce à leur excellente rigidité, ces boîtes
                s&apos;empilent de manière stable et géométrique
                dans le camion de transport, ce qui empêche tout
                glissement dans les virages et optimise l&apos;espace
                disponible.
              </p>
            </div>

            {/* Card 3 bandeau full width : ancrage / sécurité */}
            <div className="sm:col-span-2 relative bg-[#0f1d2b] text-white rounded-2xl p-6 md:p-7 overflow-hidden">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center shrink-0 h-12 w-12 rounded-xl bg-[#FF5722] text-white">
                  <Anchor className="h-6 w-6" strokeWidth={2.4} />
                </span>
                <div>
                  <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#FF5722]">
                    Sécurité manutention
                  </div>
                  <h3 className="mt-1 font-display font-extrabold text-[18px] text-white leading-tight">
                    Aucun risque d&apos;affaissement au levage
                  </h3>
                  <p className="mt-3 text-[13.5px] text-white/80 leading-relaxed">
                    Bien remplis et bien fermés, nos cartons
                    renforcés conservent leur intégrité structurelle
                    même sous une charge maximale, pour préserver le
                    dos de vos déménageurs.
                  </p>
                  <Link
                    to="/cartons-demenagement"
                    className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white text-[#0f1d2b] hover:bg-[#EEF3F7] font-bold text-[13px] transition-colors"
                    data-testid="city-crespieres-cta-renforces"
                  >
                    <Weight className="h-4 w-4" strokeWidth={2.6} />
                    Voir les cartons renforcés
                    <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Commande en ligne ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Boutique en ligne
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Commandez vos cartons en quelques clics
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous éviter de courir les magasins de bricolage des
            Yvelines et de perdre de précieuses heures, notre boutique
            en ligne regroupe l&apos;intégralité des équipements
            indispensables.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <ListChecks className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Une offre complète et centralisée
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Des cartons standards aux modèles penderie pour vos
              vêtements sur cintres, en passant par le papier bulle
              et les rubans adhésifs à forte adhérence, tout est
              disponible sur une seule plateforme.
            </p>
            <Link
              to="/cartons-demenagement"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[13px] transition-colors"
              data-testid="city-crespieres-cta-catalogue"
            >
              <Package className="h-4 w-4" strokeWidth={2.6} />
              Parcourir le catalogue
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <Boxes className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Packs clés en main
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nos kits de déménagement sont spécialement dimensionnés
              par des experts pour correspondre au volume exact de
              votre logement, du studio à la grande maison familiale.
            </p>
            <Link
              to="/kits-cartons"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[13px] transition-colors"
              data-testid="city-crespieres-cta-kits"
            >
              <ShoppingCart className="h-4 w-4" strokeWidth={2.6} />
              Voir les kits clé-en-main
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-crespieres-cta-back-dept"
        >
          <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
